import React, { useState, useEffect, useMemo } from "react";
import { observer } from "mobx-react";
import { WIDGET_META, CATEGORY_LABEL as CAT_LABEL } from "../utils/widgetConstants";
import { signalsOn } from "Pages/Dashboard/signals/signalsGate";
import dashboardV2Store from "../store/dashboardV2Store";
import WidgetCatalogLoader from "./WidgetCatalogLoader";
import WidgetPreview from "./WidgetPreview";
import ConfigureWidgetPanel from "./ConfigureWidgetPanel";
import userDetail from "Stores/userDetail";

const WidgetCatalog = observer(() => {
  const [cat, setCat] = useState("All");
  const [search, setSearch] = useState("");
  const [hovId, setHovId] = useState(null);
  const [configuringWidget, setConfiguringWidget] = useState(null);

  const {
    activeDashboard = null,
    widgetCatalog = [],
    isLoadingCatalog = false,
    addingWidgetId = null
  } = dashboardV2Store;

  // Signals widgets are offered only when Signals are on for the team.
  const catalog = widgetCatalog.filter(w => w.category !== "SIGNALS" || signalsOn());

  const addedWidgetIds = new Set(
    activeDashboard?.widgets?.map(w => w.widgetId) || []
  );

  const availableCats = useMemo(() => {
    const cats = new Set(catalog.map(w => w.category).filter(Boolean));
    return ["All", ...["EMAIL", "CALLING", "LINKEDIN", "TASKS", "OVERVIEW", "CONTACTS", "SIGNALS"].filter(c => cats.has(c))];
  }, [catalog]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = catalog.filter(w => {
    const matchesCat = cat === "All" || w.category === cat;
    const matchesSearch = (w.name || "").toLowerCase().includes(search.toLowerCase());
    return matchesCat && matchesSearch;
  });

  const hovWidget = hovId ? filtered.find(w => w.id === hovId) : null;
  const previewWidget = hovWidget || filtered[0] || null;
  const previewMeta = previewWidget ? WIDGET_META[previewWidget.id] : null;

  const handleAdd = widget => {
    if (!widget.id) return;
    // Self-loading widgets (account scores) are team-wide: there is no member scope to configure.
    if (!!userDetail.userInfo.isTeamAdmin && !WIDGET_META[widget.id]?.selfLoading) {
      setConfiguringWidget(widget);
      return;
    }
    dashboardV2Store.addWidget(widget.id, widget.category);
  };

  const handleConfirmConfigured = async audienceScope => {
    if (!configuringWidget) return;
    await dashboardV2Store.addWidget(configuringWidget.id, configuringWidget.category, audienceScope);
    setConfiguringWidget(null);
  };

  const handleRemove = widget => {
    dashboardV2Store.removeWidget(widget.id);
  };

  useEffect(() => {
    dashboardV2Store.fetchWidgetCatalog();
  }, []);

  return (
    <div
      className="widgetCatalogOverlay"
      onClick={() => dashboardV2Store.setShowWidgetCatalog(false)}
    >
      <div
        className="widgetCatalogModal"
        onClick={e => e.stopPropagation()}
      >
        <div className="widgetCatalogModalHeader">
          <div className="widgetCatalogModalHeaderTop">
            <div>
              <p className="widgetCatalogModalTitle">Add a widget</p>
              <p className="widgetCatalogModalSubtitle">
                Choose from our pre-built widgets. Each widget runs its own query.
              </p>
            </div>
            <button
              type="button"
              className="widgetCatalogCloseBtn"
              onClick={() => dashboardV2Store.setShowWidgetCatalog(false)}
            >
              ×
            </button>
          </div>
          <div className="widgetCatalogModalFilters">
            <input
              className="widgetCatalogSearch"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search widgets…"
            />
            <div className="widgetCatalogCatTabs">
              {availableCats.map(c => (
                <button
                  key={c}
                  type="button"
                  className={`widgetCatalogCatTab${cat === c ? " widgetCatalogCatTabActive" : ""}`}
                  onClick={() => setCat(c)}
                >
                  {c === "All" ? "All" : CAT_LABEL[c]}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="widgetCatalogModalBody">
          <div className="widgetCatalogList">
            {isLoadingCatalog ? (
              <WidgetCatalogLoader />
            ) : filtered.length === 0 ? (
              <p className="widgetCatalogEmptyMsg">No widgets match &ldquo;{search}&rdquo;</p>
            ) : (
              <div className="widgetCatalogGrid">
                {filtered.map(widget => {
                  const widgetId = widget.id;
                  const isAdded = addedWidgetIds.has(widgetId);
                  const isHov = hovId === widgetId;
                  return (
                    <div
                      key={widgetId}
                      className={`widgetCatalogCard${isHov ? " widgetCatalogCardHov" : ""}`}
                      onMouseEnter={() => setHovId(widgetId)}
                      onMouseLeave={() => setHovId(null)}
                    >
                      <div className="widgetCatalogCardTop">
                        <span className="widgetCatalogCatBadge">
                          {CAT_LABEL[widget.category] || widget.category}
                        </span>
                      </div>
                      <p className="widgetCatalogCardTitle">{widget.name}</p>
                      {WIDGET_META[widgetId]?.metrics?.length > 0 && (
                        <div className="widgetCatalogMetricTags">
                          {WIDGET_META[widgetId].metrics.slice(0, 3).map(m => (
                            <span key={m} className="widgetCatalogMetricTag">{m}</span>
                          ))}
                          {WIDGET_META[widgetId].metrics.length > 3 && (
                            <span className="widgetCatalogMetricTagMore">+{WIDGET_META[widgetId].metrics.length - 3}</span>
                          )}
                        </div>
                      )}
                      {isAdded ? (
                        <div className="widgetCatalogCardActions">
                          <div className="widgetCatalogAddedBadge">✓ Added</div>
                          <button
                            type="button"
                            className="widgetCatalogRemoveBtn"
                            onClick={e => { e.stopPropagation(); handleRemove(widget); }}
                          >
                            Remove
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className={`widgetCatalogAddBtn${isHov && !addingWidgetId ? " widgetCatalogAddBtnHov" : ""}${addingWidgetId === widgetId ? " widgetCatalogAddBtnLoading" : ""}`}
                          onClick={e => { e.stopPropagation(); handleAdd(widget); }}
                          disabled={!!addingWidgetId}
                        >
                          {addingWidgetId === widgetId ? "Adding…" : "+ Add widget"}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="widgetCatalogPreview">
            {configuringWidget ? (
              <ConfigureWidgetPanel
                widget={configuringWidget}
                onBack={() => setConfiguringWidget(null)}
                onConfirm={handleConfirmConfigured}
                isAdding={addingWidgetId === configuringWidget.id}
              />
            ) : previewWidget ? (
              <>
                <p className="widgetCatalogPreviewLabel">Preview</p>
                <div className="widgetCatalogPreviewCard">
                  <div className="widgetCatalogPreviewCardHeader">
                    <p className="widgetCatalogPreviewTitle">{previewWidget.name}</p>
                    <span className="widgetCatalogCatBadge">{CAT_LABEL[previewWidget.category] || previewWidget.category}</span>
                  </div>
                  <div className="widgetCatalogPreviewContent">
                    <WidgetPreview widgetId={previewWidget.id} />
                  </div>
                </div>
                <p className="widgetCatalogPreviewHint">
                  Sample data shown for preview only.
                </p>
              </>
            ) : (
              <p className="widgetCatalogPreviewEmpty">Hover a widget to preview</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

export default WidgetCatalog;
